import { Flex } from '@buffer-mono/popcorn'

import type { useCommentsChannels } from '~publish/components/CommentsSidebar/useCommentsChannels'

import { MentionContextProvider } from '../Mentions/context/MentionContext'
import { MentionThreadContent } from '../Mentions/MentionCard/MentionThreadContent'
import { MentionsContextSection } from '../Mentions/MentionsContextSection'
import { MentionPostMiniature } from '../Mentions/MentionPostMiniature'
import type { MentionThreadData } from './types'

import styles from './MentionDetailsContent.module.css'

import type { JSX } from 'react'

type Channel = ReturnType<typeof useCommentsChannels>['channels'][number]

type MentionDetailsContentProps = {
  mention: NonNullable<MentionThreadData['mention']>
  post: MentionThreadData['post']
  context: MentionThreadData['context']
  replies: MentionThreadData['replies']
  channel: Channel
}

/**
 * The body of the mention-details dialog: the thread-root post the mention
 * belongs to (when there is one), the mention's ancestors (collapsed behind
 * the same "Show more replies" expansion as the inbox card), and the mention
 * itself with its replies and the reply form.
 *
 * Reuses the inbox thread components so the dialog stays visually and
 * behaviorally consistent with the Community inbox.
 */
export function MentionDetailsContent({
  mention,
  post,
  context,
  replies,
  channel,
}: MentionDetailsContentProps): JSX.Element {
  // The thread root is itself a `mentionType: post` mention. Skip it when it
  // resolves to the mention we're already showing (post-kind mentions).
  const showPostMiniature = post !== null && post.id !== mention.id

  return (
    <Flex direction="column" gap="md" className={styles.container}>
      {showPostMiniature && (
        <MentionContextProvider mention={post}>
          <div className={styles.post}>
            <MentionPostMiniature channel={channel} />
          </div>
        </MentionContextProvider>
      )}
      <div className={styles.thread}>
        <MentionContextProvider mention={mention} replies={replies}>
          <MentionsContextSection
            context={context}
            loading={false}
            channel={channel}
          />
          <MentionThreadContent
            channel={channel}
            expanded
            threadPosition={context.length > 0 ? 'continued' : 'start'}
          />
        </MentionContextProvider>
      </div>
    </Flex>
  )
}
